<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Tag from 'primevue/tag';
import Card from 'primevue/card';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import axios from 'axios';

defineOptions({
    layout: AdminMainLayout,
});

const props = defineProps({
    banners: Object,
    filters: Object,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const confirm = useConfirm();

// Filters
const search = ref(props.filters?.search || '');
const statusFilter = ref(props.filters?.is_active);
const audienceFilter = ref(props.filters?.audience);

// Table sorting
const sortField = ref('created_at');
const sortOrder = ref(-1);

// Audience options
const audienceOptions = [
    { label: 'All', value: null },
    { label: 'Both', value: 'both' },
    { label: 'Admin', value: 'admin' },
    { label: 'Client', value: 'client' },
];

const statusOptions = [
    { label: 'All', value: null },
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
];

function applyFilters() {
    router.get(route('content.banners.index'), {
        search: search.value || undefined,
        is_active: statusFilter.value,
        audience: audienceFilter.value || undefined,
        sortField: sortField.value,
        sortOrder: sortOrder.value === 1 ? 'asc' : 'desc',
    }, {
        preserveState: true,
        preserveScroll: true,
    });
}

function toggleBanner(banner) {
    axios.post(route('content.banners.toggle', banner.id))
        .then(() => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: `Banner ${banner.is_active ? 'deactivated' : 'activated'}`,
                life: 3000,
            });
            router.reload({ preserveScroll: true });
        })
        .catch(() => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to toggle banner status',
                life: 3000,
            });
        });
}

function deleteBanner(banner) {
    confirm.require({
        message: `${$t('Are you sure you want to delete')} "${banner.title}"?`,
        header: `${$t('Delete Banner')}`,
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
            router.delete(route('content.banners.destroy', banner.id), {
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Banner deleted successfully',
                        life: 3000,
                    });
                },
                onError: () => {
                    toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete banner',
                        life: 3000,
                    });
                },
            });
        },
    });
}

function getLayoutBadgeSeverity(layout) {
    const severities = {
        'image-bg': 'primary',
        'image-left': 'info',
        'image-right': 'warning',
    };
    return severities[layout] || 'secondary';
}

function getStatusSeverity(isActive) {
    return isActive ? 'success' : 'danger';
}

function getAudienceSeverity(audience) {
    const severities = {
        'both': 'primary',
        'admin': 'warning',
        'client': 'info',
    };
    return severities[audience] || 'secondary';
}

function onSort(event) {
    sortField.value = event.sortField;
    sortOrder.value = event.sortOrder;
    applyFilters();
}
</script>

<template>
    <div>
        <Head :title="$t('Banner Management')" />

        <div class="grid">
            <div class="col-12">
                <Card>
                    <template #title>
                        <div class="flex flex-grow align-items-center justify-content-between">
                            <span>{{ $t('Banner Management') }}</span>
                            <Link :href="route('content.banners.create')" class="ml-auto">
                                <Button :label="$t('New Banner')" icon="pi pi-plus" severity="primary" />
                            </Link>
                        </div>
                    </template>

                    <template #content>

                        <!-- Data Table -->
                        <DataTable
                            :value="banners.data"
                            :paginator="true"
                            :rows="15"
                            :totalRecords="banners.total"
                            :lazy="true"
                            @sort="onSort"
                            responsiveLayout="scroll"
                        >
                            <Column field="id" header="ID" :sortable="true" style="width: 5%" />

                            <Column field="title" :header="$t('Title')" :sortable="true">
                                <template #body="{ data }">
                                    <div>
                                        <div class="font-semibold">{{ data.title }}</div>
                                        <div v-if="data.subtitle" class="text-sm text-color-secondary">
                                            {{ data.subtitle }}
                                        </div>
                                    </div>
                                </template>
                            </Column>

                            <Column field="layout" :header="$t('Layout')" :sortable="true">
                                <template #body="{ data }">
                                    <Tag :value="data.layout" :severity="getLayoutBadgeSeverity(data.layout)" />
                                </template>
                            </Column>

                            <Column field="audience" :header="$t('Audience')" :sortable="true">
                                <template #body="{ data }">
                                    <Tag :value="data.audience" :severity="getAudienceSeverity(data.audience)" />
                                </template>
                            </Column>

                            <Column field="targets" :header="$t('Targets')">
                                <template #body="{ data }">
                                    <Tag :value="data.targets" severity="info" />
                                </template>
                            </Column>

                            <Column field="is_active" :header="$t('Status')" :sortable="true">
                                <template #body="{ data }">
                                    <Tag
                                        :value="data.is_active ? $t('Active') : $t('Inactive')"
                                        :severity="getStatusSeverity(data.is_active)"
                                    />
                                </template>
                            </Column>


                            <Column :header="$t('Actions')" style="width: 15%">
                                <template #body="{ data }">
                                    <div class="flex gap-2">
                                        <Button
                                            :icon="data.is_active ? 'pi pi-eye-slash' : 'pi pi-eye'"
                                            :severity="data.is_active ? 'warning' : 'success'"
                                            text
                                            rounded
                                            size="small"
                                            @click="toggleBanner(data)"
                                            v-tooltip.top="data.is_active ? 'Deactivate' : 'Activate'"
                                        />
                                        <Link :href="route('content.banners.edit', data.id)">
                                            <Button
                                                icon="pi pi-pencil"
                                                severity="info"
                                                text
                                                rounded
                                                size="small"
                                                v-tooltip.top="$t('Edit')"
                                            />
                                        </Link>
                                        <Button
                                            icon="pi pi-trash"
                                            severity="danger"
                                            text
                                            rounded
                                            size="small"
                                            @click="deleteBanner(data)"
                                            v-tooltip.top="$t('Delete')"
                                        />
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>
            </div>
        </div>

        <ConfirmDialog />
    </div>
</template>
